import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { topics, topicMap } from "@/mocks/topics";
import { articleMap, type Article } from "@/mocks/articles";
import { useSeo } from "@/hooks/useSeo";

export default function Topic() {
  const { topicId } = useParams();
  const topic = topicId ? topicMap[topicId] : undefined;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [topicId]);

  const grouped = useMemo(() => {
    if (!topic) return [];
    const result: { categoryName: string; items: Article[] }[] = [];
    topic.articleIds.forEach((articleId) => {
      const article = articleMap[articleId];
      if (!article) return;
      const found = result.find((group) => group.categoryName === article.categoryName);
      if (found) {
        found.items.push(article);
      } else {
        result.push({ categoryName: article.categoryName, items: [article] });
      }
    });
    return result;
  }, [topic]);

  useSeo({
    title: topic ? `${topic.title} - 黑人文化专题 - 黑人` : "专题未找到 - 黑人文化资讯",
    description: topic ? topic.intro : "未找到对应专题，请返回首页浏览更多黑人文化内容。",
    keywords: topic ? `${topic.title},${topic.subtitle},黑人文化专题,黑人资讯` : "黑人文化专题,黑人资讯",
  });

  if (!topic) {
    return (
      <div className="mx-auto max-w-content px-4 md:px-8 py-20 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
          <i className="ri-bookmark-line text-3xl" />
        </div>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-900">专题不存在</h1>
        <p className="mt-3 text-sm text-foreground-500">你访问的专题可能已被移动或删除。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const relatedTopics = topic.relatedTopicIds
    .map((relatedId) => topicMap[relatedId])
    .filter(Boolean);

  return (
    <div>
      <div className="relative h-[420px] md:h-[520px] overflow-hidden">
        <img
          src={topic.cover}
          alt={topic.title}
          title={`${topic.title}专题 - 黑人文化资讯`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/75 to-primary-950/45" />
        <div className="relative z-10 mx-auto max-w-content h-full px-4 md:px-8 flex flex-col justify-end pb-10 md:pb-14">
          <span className="self-start text-xs px-3 py-1 rounded-full bg-accent-500 text-secondary-950 font-medium mb-4 whitespace-nowrap">
            文化专题 · {topic.articleIds.length} 篇内容
          </span>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight">
            {topic.title}
          </h1>
          <p className="mt-2 text-sm md:text-base text-accent-400 font-medium">{topic.subtitle}</p>
          <p className="mt-5 text-sm md:text-base text-background-200 leading-relaxed max-w-3xl">
            {topic.intro}
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-content px-4 md:px-8 py-8 md:py-12">
        <div className="bg-background-50 rounded-lg border border-background-200 px-4 py-3 mb-10">
          <Breadcrumb items={[{ label: "专题" }, { label: topic.title }]} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            {grouped.map((group) => (
              <div key={group.categoryName} className="mb-12 last:mb-0">
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-1.5 h-6 rounded-full bg-accent-500" />
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                    {group.categoryName}
                  </h2>
                  <span className="text-sm text-foreground-400">{group.items.length} 篇</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {group.items.map((article) => (
                    <ArticleCard key={article.id} article={article} variant="default" />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <aside className="lg:col-span-4 space-y-6">
            <div className="bg-accent-50 rounded-lg border border-accent-200 p-5 md:p-6">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-sparkling-2-line text-accent-600 text-lg" />
                <h3 className="font-heading text-lg font-semibold text-foreground-900">专题亮点</h3>
              </div>
              <ul className="space-y-3">
                {topic.highlights.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-foreground-700 leading-relaxed">
                    <i className="ri-checkbox-circle-line text-accent-600 mt-0.5 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-background-100/70 rounded-lg border border-background-200 p-5 md:p-6">
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-bookmark-3-line text-primary-600 text-lg" />
                <h3 className="font-heading text-lg font-semibold text-foreground-900">相关专题</h3>
              </div>
              <div className="space-y-3">
                {relatedTopics.map((item) => (
                  <Link
                    key={item.id}
                    to={`/topic/${item.id}`}
                    className="group flex items-center gap-3 p-3 rounded-md bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="w-14 h-14 rounded-md overflow-hidden shrink-0 bg-background-200">
                      <img
                        src={item.cover}
                        alt={item.title}
                        title={`${item.title}专题`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0">
                      <span className="block text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </span>
                      <span className="block text-xs text-foreground-400 mt-0.5 truncate">
                        {item.subtitle}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="bg-primary-600 rounded-lg p-5 md:p-6 text-background-50">
              <h3 className="font-heading text-lg font-semibold mb-3">浏览全部专题</h3>
              <p className="text-sm text-primary-100 leading-relaxed mb-4">
                更多围绕文化主题的深度内容，正在持续更新中。
              </p>
              <div className="space-y-2">
                {topics
                  .filter((item) => item.id !== topic.id)
                  .map((item) => (
                    <Link
                      key={item.id}
                      to={`/topic/${item.id}`}
                      className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-md bg-primary-700/50 hover:bg-primary-700 transition-colors cursor-pointer"
                    >
                      <span className="text-sm">{item.title}</span>
                      <i className="ri-arrow-right-line text-accent-400 shrink-0" />
                    </Link>
                  ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}